import React, { useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "../ui/sheet";
import { NightCreator } from "./NightCreator";
import { INTENSITY, SERIF, SLOT_LABEL, enablePush, startPlan } from "./practiceApi";

const SLOTS = { light: ["morning"], standard: ["morning", "evening"], deep: ["morning", "midday", "evening"] };
const DEFAULTS = { morning: "07:30", midday: "13:00", evening: "18:30", night: "22:00" };
const Step = ({ n, title, children }) => (
  <section className="mt-7"><p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#d7bb7a]">{n}</p>
    <h3 className="mt-1 text-xl text-[#f3ecd9]" style={{ fontFamily: SERIF }}>{title}</h3><div className="mt-3">{children}</div></section>
);

// Installation → 21-Day Practice in one sheet: intensity → night (optional) → gentle reminders → Begin.
export const PracticeSheet = ({ open, onOpenChange, journeyId, onStarted, initial }) => {
  const [intensity, setIntensity] = useState(initial?.intensity || "standard");
  const [night, setNight] = useState(initial?.night ? true : null);
  const [remind, setRemind] = useState(!!initial?.reminders);
  const [times, setTimes] = useState({ ...DEFAULTS, ...(initial?.times || {}) });
  const [busy, setBusy] = useState(false);
  const slots = [...SLOTS[intensity], ...(night ? ["night"] : [])];
  const begin = async () => {
    setBusy(true);
    try {
      if (remind) {
        const r = await enablePush().catch(() => "unsupported");
        if (r !== "ok") toast.message("Reminders need notifications on. On iPhone, add Soulumo to your Home Screen first, then turn them on.");
      }
      const p = await startPlan({ journey_id: journeyId, intensity, night: !!night, reminders: remind, times });
      toast.success("Your 21-Day Practice has begun");
      onStarted?.(p); onOpenChange(false);
      window.dispatchEvent(new Event("soulumo:practice"));
      setTimeout(() => {
        const card = document.querySelector('[data-testid="practice-card"]');
        if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }, 400);
    } catch { toast.error("Couldn't start your practice. Please try again."); }
    setBusy(false);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto border-[#2b3d5e] bg-[#0b1a33] px-6 pb-10 text-[#f3ecd9]" data-testid="practice-sheet">
        <h2 className="mt-2 text-3xl text-[#f3ecd9]" style={{ fontFamily: SERIF }}>Your 21-Day Practice</h2>
        <p className="mt-2 text-sm text-[#cfc6ae]">Let these words become familiar, during your day and while you rest.</p>
        <Step n="One" title="How often would you like to listen?">
          <div className="grid gap-2">
            {INTENSITY.map(([id, label, note]) => (
              <button key={id} type="button" onClick={() => setIntensity(id)} data-testid={`practice-intensity-${id}`}
                className={`flex items-baseline justify-between rounded-2xl border px-4 py-3 text-left transition-colors ${intensity === id ? "border-[#d7bb7a] bg-[#d7bb7a]/10" : "border-[#2b3d5e] hover:border-[#d7bb7a]/50"}`}>
                <span className="text-lg" style={{ fontFamily: SERIF }}>{label}</span><span className="text-xs text-[#b9ad8f]">{note}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-[#8f8a78]">More listens simply means more moments in your day with your words.</p>
        </Step>
        <Step n="Two" title="Add a Night Installation?">
          <p className="text-sm text-[#cfc6ae]">A gentle 3-hour version of your installation, to accompany rest and sleep. Always optional.</p>
          {night === null && (
            <div className="mt-3 flex gap-2">
              <button type="button" onClick={() => setNight(true)} className="rounded-full border border-[#d7bb7a] px-5 py-2 text-xs uppercase tracking-[0.2em] text-[#f3ecd9] hover:bg-[#d7bb7a]/20" data-testid="practice-night-yes">Yes</button>
              <button type="button" onClick={() => setNight(false)} className="rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] text-[#b9ad8f] hover:text-[#f3ecd9]" data-testid="practice-night-no">Not now</button>
            </div>
          )}
          {night && <div className="mt-3"><NightCreator journeyId={journeyId} compact /></div>}
          {night === false && <p className="mt-2 text-xs text-[#8f8a78]" data-testid="practice-night-skipped">No problem. You can add it any time.</p>}
        </Step>
        <Step n="Three" title="Would you like gentle reminders?">
          <label className="flex items-center gap-3 text-sm" data-testid="practice-remind-toggle">
            <input type="checkbox" checked={remind} onChange={(e) => setRemind(e.target.checked)} className="h-4 w-4 accent-[#d7bb7a]" /> Remind me
          </label>
          {remind && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {slots.map((s) => (
                <label key={s} className="flex items-center justify-between rounded-xl border border-[#2b3d5e] px-3 py-2 text-sm">{SLOT_LABEL[s]}
                  <input type="time" value={times[s]} onChange={(e) => setTimes({ ...times, [s]: e.target.value })} className="bg-transparent text-[#f3ecd9]" data-testid={`practice-time-${s}`} />
                </label>
              ))}
            </div>
          )}
        </Step>
        <button type="button" onClick={begin} disabled={busy} data-testid="practice-begin-btn"
          className="mt-9 w-full rounded-full bg-[#d7bb7a] py-3.5 text-xs uppercase tracking-[0.3em] text-[#0b1a33] transition-opacity hover:opacity-90 disabled:opacity-50">
          {busy ? "Beginning…" : "Begin"}
        </button>
      </SheetContent>
    </Sheet>
  );
};
